import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box, Typography, Chip, Grid, Skeleton, Button, Card, CardContent, alpha,
  useTheme, Stack, Divider, Avatar, Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import { useGetAttendanceSessionReportQuery } from "../../Api/attoDeskApi";
import GradeSubjectsDialog, { summarizeGradesCompact } from "../AttendanceSession/GradeSubjectsDialog";
import useUser from "../../Core/hook/useUser";

/**
 * Class Session Details — /class-sessions/:occurrenceId/details.
 *
 * The premium read-first "full details" page for one specific class
 * occurrence. Deliberately separate from:
 *
 *   /class-sessions/:occurrenceId/attendance   → attendance ENTRY workspace
 *   /attendance/report/:occurrenceId           → attendance REPORT (submitted history)
 *
 * All three routes address the same occurrence but represent
 * different user intents; keeping them distinct removes the "Today
 * tile always opens the report" UX bug.
 *
 * Data source reuses `useGetAttendanceSessionReportQuery` because
 * it's already the canonical per-occurrence DTO on the backend
 * (Phase-2 enriched with scheduled/actual ISO timestamps, mode,
 * location, ended_by, grade_subjects, roster + attendance summary).
 * We render only the CLASS-INFORMATION half — no trend chart, no
 * highest-attendance ranking, no best-attended-classes lists (those
 * belong to Attendance Report). Zero backend changes.
 */

interface GradeSubjectsBucket {
  grade_id: number;
  grade: { id: number; name: string; sort_order?: number | null };
  subjects: Array<{ id: number; name: string; sort_order?: number | null }>;
}
interface StudentRow {
  id: number;
  name: string;
  grade: string | null;
  avatar: string | null;
  status: "present" | "absent" | "late" | "not_marked";
}
interface Report {
  session: {
    id: number;
    class_id: number;
    class_name: string;
    grade_subjects: GradeSubjectsBucket[];
    date: string;
    start_time: string;
    end_time: string;
    scheduled_start_at: string;
    scheduled_end_at: string;
    actual_start_at: string | null;
    actual_end_at: string | null;
    ended_by: "manual" | "system" | null;
    late_start_minutes: number | null;
    status: "scheduled" | "completed" | "cancelled" | "rescheduled";
    location: string | null;
    mode: string | null;
    staff_name: string | null;
    staff_photo: string | null;
  };
  summary: {
    total: number; enrolled: number; present: number; absent: number; late: number;
    not_recorded: number; attendance_rate: number; submitted: boolean;
  };
  students: StudentRow[];
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const fmtTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—";
const fmtDuration = (ms: number) => {
  if (ms <= 0) return "—";
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h} hr` : `${h} hr ${rem} min`;
};

const compactClassLabel = (session: Report["session"]): string => {
  const raw = (session.class_name || "").trim();
  const looksLikeGradeDump = /,.*,.*,/.test(raw);
  const gs = session.grade_subjects || [];
  if (raw && !looksLikeGradeDump) return raw;
  if (gs.length === 0) return raw || "Class Session";
  const { gradesLabel } = summarizeGradesCompact(gs);
  const gradeCount = gs.length;
  const uniqueSubjects = new Set(gs.flatMap((g) => g.subjects.map((s) => s.id))).size;
  const single = gradeCount === 1 ? gs[0] : null;
  if (single && single.subjects.length <= 3) {
    return `${gradesLabel} — ${single.subjects.map((s) => s.name).join(", ")}`;
  }
  return `${gradeCount} Grade${gradeCount === 1 ? "" : "s"} · ${uniqueSubjects} Subject${uniqueSubjects === 1 ? "" : "s"}`;
};

const statusPill = (status: string, actualStart: string | null) => {
  if (status === "cancelled") return { label: "Cancelled", bg: "#fee2e2", fg: "#991b1b", border: "#fecaca" };
  if (status === "completed") return { label: "Completed", bg: "#d1fae5", fg: "#065f46", border: "#a7f3d0" };
  if (actualStart)            return { label: "Live",      bg: "#dcfce7", fg: "#166534", border: "#86efac" };
  return                              { label: "Upcoming", bg: "#e0e7ff", fg: "#3730a3", border: "#c7d2fe" };
};

const rowStatusColor = (s: string) => {
  if (s === "present") return { fg: "#16a34a", bg: "#f0fdf4", label: "Present" };
  if (s === "absent")  return { fg: "#dc2626", bg: "#fef2f2", label: "Absent" };
  if (s === "late")    return { fg: "#d97706", bg: "#fffbeb", label: "Late" };
  return                     { fg: "#64748b", bg: "#f1f5f9", label: "Not Recorded" };
};

const ClassSessionDetails: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { occurrenceId } = useParams<{ occurrenceId: string }>();
  const [searchParams] = useSearchParams();
  const [subjectsOpen, setSubjectsOpen] = useState(false);
  const { isSystemAdmin } = useUser();

  // `?return_url=` carries the origin surface (Today, staff schedule,
  // etc.) so Back lands the user where they came from. Same-origin
  // safety — only relative paths accepted, never a foreign URL.
  const returnUrl = searchParams.get("return_url") || null;
  const goBack = () => {
    if (returnUrl && returnUrl.startsWith("/") && !returnUrl.startsWith("//")) {
      navigate(returnUrl);
    } else {
      navigate("/today");
    }
  };

  const { data, isLoading, error } = useGetAttendanceSessionReportQuery(
    { sessionId: occurrenceId ?? "" },
    { skip: !occurrenceId },
  );
  const report: Report | null = useMemo(() => (data as any)?.data ?? null, [data]);

  const isForbidden = (error as any)?.status === 403;
  const isMissing = (error as any)?.status === 404;

  if (isForbidden) {
    return (
      <Box sx={{ minHeight: "60vh", p: 4, bgcolor: "#f0f4f8" }}>
        <Button onClick={goBack} startIcon={<ArrowBackIcon />} sx={{ textTransform: "none", mb: 2 }}>Back</Button>
        <Card sx={{ borderRadius: 3, textAlign: "center", py: 6, maxWidth: 480, mx: "auto" }}>
          <CardContent>
            <Typography sx={{ fontSize: 18, fontWeight: 800 }}>You don't have access to this class.</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }
  if (isMissing) {
    return (
      <Box sx={{ minHeight: "60vh", p: 4, bgcolor: "#f0f4f8" }}>
        <Button onClick={goBack} startIcon={<ArrowBackIcon />} sx={{ textTransform: "none", mb: 2 }}>Back</Button>
        <Card sx={{ borderRadius: 3, textAlign: "center", py: 6, maxWidth: 480, mx: "auto" }}>
          <CardContent>
            <Typography sx={{ fontSize: 18, fontWeight: 800 }}>Class session not found.</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const session = report?.session;
  const summary = report?.summary;
  const students = report?.students ?? [];

  const status = session ? statusPill(session.status, session.actual_start_at) : null;
  const schedStart = session ? new Date(session.scheduled_start_at).getTime() : 0;
  const schedEnd   = session ? new Date(session.scheduled_end_at).getTime()   : 0;
  const schedDur   = session ? schedEnd - schedStart : 0;
  const actStart   = session?.actual_start_at ? new Date(session.actual_start_at).getTime() : null;
  const lateStart  = actStart != null && schedStart > 0 ? Math.max(0, actStart - schedStart) : 0;

  return (
    <Box sx={{ bgcolor: "#f0f4f8", minHeight: "100vh", pb: 5 }}>
      {/* ── HERO ── */}
      <Box sx={{
        background: `linear-gradient(135deg, #10243E 0%, #1e3a5f 45%, #0C5A46 100%)`,
        color: "#fff", px: { xs: 3, md: 5 }, pt: { xs: 3, md: 4 }, pb: { xs: 3, md: 4 },
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{
          position: "absolute", top: -70, right: -50, width: 260, height: 260,
          background: "radial-gradient(circle, rgba(34,166,153,0.28) 0%, transparent 65%)",
          pointerEvents: "none",
        }} />
        <Button
          onClick={goBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            textTransform: "none", color: "#fff", opacity: 0.85, mb: 1,
            "&:hover": { opacity: 1, bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          Back to Today
        </Button>
        <Typography sx={{ fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 800, opacity: 0.72 }}>
          Class Details
        </Typography>
        {isLoading || !session ? (
          <Skeleton height={44} width={340} sx={{ bgcolor: "rgba(255,255,255,0.2)", mt: 0.5 }} />
        ) : (
          <>
            <Typography sx={{ fontSize: { xs: 22, md: 30 }, fontWeight: 800, letterSpacing: "-0.01em", mt: 0.5 }}>
              {compactClassLabel(session)}
            </Typography>
            <Typography sx={{ fontSize: 13.5, opacity: 0.85, mt: 0.5 }}>{fmtDate(session.date)}</Typography>

            <Stack direction="row" spacing={2.5} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1.25 }}>
              {session.staff_name && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ width: 30, height: 30, bgcolor: "rgba(255,255,255,0.14)", fontSize: 12, fontWeight: 700 }}>
                    {session.staff_name.slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: 10, letterSpacing: 0.6, textTransform: "uppercase", opacity: 0.7, fontWeight: 700 }}>Teacher</Typography>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{session.staff_name}</Typography>
                  </Box>
                </Stack>
              )}
              {session.location && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocationOnOutlinedIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{session.location}</Typography>
                </Stack>
              )}
              {session.mode && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <LaptopMacOutlinedIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{session.mode}</Typography>
                </Stack>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <ScheduleOutlinedIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                  {fmtTime(session.scheduled_start_at)} – {fmtTime(session.scheduled_end_at)}
                </Typography>
              </Stack>
            </Stack>

            {/* Status chips */}
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
              {status && (
                <Chip
                  size="small"
                  label={status.label}
                  sx={{ bgcolor: status.bg, color: status.fg, border: `1px solid ${status.border}`, fontWeight: 800, fontSize: 11 }}
                />
              )}
              {session.ended_by === "system" && (
                <Chip
                  size="small"
                  icon={<AutorenewOutlinedIcon sx={{ fontSize: "12px !important", color: "#F59E0B !important" }} />}
                  label="Auto-Completed"
                  sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 800, fontSize: 11 }}
                />
              )}
              {(session.grade_subjects?.length ?? 0) > 0 && (
                <Button
                  onClick={() => setSubjectsOpen(true)}
                  startIcon={<PsychologyOutlinedIcon />}
                  sx={{
                    textTransform: "none", fontWeight: 700, fontSize: 11.5,
                    color: "#fff", bgcolor: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    borderRadius: 999, py: 0.25, minHeight: 26,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                  }}
                >
                  View Grades &amp; Subjects
                </Button>
              )}
            </Stack>
          </>
        )}
      </Box>

      {/* ── ACTIONS ── */}
      <Box sx={{ px: { xs: 2, md: 5 }, mt: 3 }}>
        <Stack direction="row" spacing={1.25} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          <Button
            variant="contained"
            startIcon={<PlayCircleOutlineIcon />}
            onClick={() => navigate(`/class-sessions/${occurrenceId}/attendance`)}
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2, bgcolor: "#22A699", "&:hover": { bgcolor: "#0C5A46" } }}
          >
            Open Attendance
          </Button>
          {summary?.submitted && (
            <Button
              variant="outlined"
              startIcon={<AssessmentOutlinedIcon />}
              onClick={() => navigate(`/attendance/report/${occurrenceId}?return_url=${encodeURIComponent(returnUrl || "/today")}`)}
              sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
            >
              Attendance Report
            </Button>
          )}
          {isSystemAdmin && session?.class_id && (
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={() => navigate(`/class-schedules/${session.class_id}/edit`)}
              sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
            >
              Edit Schedule
            </Button>
          )}
        </Stack>
      </Box>

      {/* ── MAIN GRID ── */}
      <Box sx={{ px: { xs: 2, md: 5 }, mt: 3 }}>
        <Grid container spacing={2}>
          {/* Timing / Status card */}
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 3, border: "1px solid #E3EAF2", boxShadow: "0 1px 4px rgba(15,23,42,0.05)" }}>
              <CardContent>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#475569", letterSpacing: 1, textTransform: "uppercase", mb: 1.25 }}>
                  Schedule
                </Typography>
                {isLoading || !session ? (
                  <Skeleton height={90} />
                ) : (
                  <>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: 10.5, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Scheduled</Typography>
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a", mt: 0.5 }}>
                          {fmtTime(session.scheduled_start_at)} – {fmtTime(session.scheduled_end_at)}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.25 }}>{fmtDuration(schedDur)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: 10.5, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Actual</Typography>
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a", mt: 0.5 }}>
                          {actStart ? fmtTime(session.actual_start_at) : "—"} – {session.actual_end_at ? fmtTime(session.actual_end_at) : "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.25 }}>
                          {actStart == null ? "Not started" : lateStart >= 60_000 ? `Started ${fmtDuration(lateStart)} late` : "On time"}
                        </Typography>
                      </Grid>
                    </Grid>
                    {session.late_start_minutes != null && session.late_start_minutes > 0 && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Chip
                          size="small"
                          label={`Late start: ${session.late_start_minutes} min`}
                          sx={{ bgcolor: "#fffbeb", color: "#854d0e", fontWeight: 800, border: "1px solid #fde68a" }}
                        />
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Capacity card */}
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 3, border: "1px solid #E3EAF2", boxShadow: "0 1px 4px rgba(15,23,42,0.05)" }}>
              <CardContent>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#475569", letterSpacing: 1, textTransform: "uppercase", mb: 1.25 }}>
                  Capacity &amp; Enrollment
                </Typography>
                {isLoading || !summary ? (
                  <Skeleton height={90} />
                ) : (
                  <Grid container spacing={2}>
                    {[
                      { label: "Enrolled",         value: summary.enrolled, color: "#1b3a6b", bg: "#eef2ff", icon: <GroupOutlinedIcon fontSize="small" /> },
                      { label: "Available Spots",  value: Math.max(0, (summary.enrolled ?? 0) - (summary.present ?? 0)) >= 0 ? (summary as any).available_spots ?? "—" : "—", color: "#0d9488", bg: "#ecfeff", icon: <EventAvailableOutlinedIcon fontSize="small" /> },
                      { label: "Present",          value: summary.present, color: "#16a34a", bg: "#f0fdf4", icon: null },
                      { label: "Absent",           value: summary.absent, color: "#dc2626", bg: "#fef2f2", icon: null },
                      { label: "Late",             value: summary.late, color: "#d97706", bg: "#fffbeb", icon: null },
                    ].map((k) => (
                      <Grid item xs={6} sm={4} md={2.4} key={k.label}>
                        <Box sx={{
                          p: 1.5, borderRadius: 2, bgcolor: k.bg,
                          border: `1px solid ${alpha(k.color, 0.2)}`,
                        }}>
                          <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: k.color, letterSpacing: 0.6, textTransform: "uppercase" }}>{k.label}</Typography>
                          <Typography sx={{ fontSize: 22, fontWeight: 800, color: k.color, lineHeight: 1, mt: 0.5, fontVariantNumeric: "tabular-nums" }}>
                            {k.value ?? "—"}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Roster */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, border: "1px solid #E3EAF2", boxShadow: "0 1px 4px rgba(15,23,42,0.05)" }}>
              <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#475569", letterSpacing: 1, textTransform: "uppercase" }}>
                  Students in this class
                </Typography>
                {summary && (
                  <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>
                    {students.length} of {summary.enrolled}
                  </Typography>
                )}
              </Box>
              {isLoading ? (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Skeleton height={40} /><Skeleton height={40} sx={{ mt: 0.5 }} />
                </Box>
              ) : students.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center", color: "text.secondary", fontSize: 13 }}>
                  No students enrolled in this class.
                </Box>
              ) : (
                students.map((s) => {
                  const st = rowStatusColor(s.status);
                  return (
                    <Box
                      key={s.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr auto", sm: "auto 1fr auto auto" },
                        gap: 1.5, alignItems: "center",
                        p: 2, borderTop: "1px solid #f1f5f9",
                      }}
                    >
                      <Avatar sx={{ width: 34, height: 34, fontSize: 12, fontWeight: 800, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, display: { xs: "none", sm: "flex" } }}>
                        {s.name.slice(0, 1).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</Typography>
                        {s.grade && <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>{s.grade}</Typography>}
                      </Box>
                      <Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.fg, fontWeight: 800, fontSize: 10.5 }} />
                      <Tooltip title="Attendance for this student" arrow>
                        <span />
                      </Tooltip>
                    </Box>
                  );
                })
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>

      {session && (
        <GradeSubjectsDialog
          open={subjectsOpen}
          onClose={() => setSubjectsOpen(false)}
          groups={session.grade_subjects || []}
          title="Grades & Subjects"
          subtitle={[
            session.staff_name ? `Teacher: ${session.staff_name}` : null,
            fmtDate(session.date),
            `${fmtTime(session.scheduled_start_at)} – ${fmtTime(session.scheduled_end_at)}`,
          ].filter(Boolean).join(" · ")}
        />
      )}
    </Box>
  );
};

export default ClassSessionDetails;
